import { isPointInPolygon } from "../isPointInPolygon";
import { doesSegmentIntersectRing } from "../doesSegmentIntersectRing";
import type { ScaledRoute } from "../../types";
import type { PlanRouteGroupV3, PlanSelectionArea } from "../../types";
import type { PageAreaGroup } from "../types";
import type { FilterExportRouteGroupsBySelectionAreas } from "./types";

// Mirrors the backend whole-route rule: a route touches an area when any node is
// inside it OR any segment crosses it; a route with no geometry never touches.
const isRouteTouchingAnyArea = (
  route: ScaledRoute,
  pageAreas: PlanSelectionArea[],
): boolean => {
  if (route.nodes.length === 0) return false;

  if (
    route.nodes.some((node) =>
      pageAreas.some((area) =>
        isPointInPolygon({ point: node, ring: area.points }),
      ),
    )
  ) {
    return true;
  }

  const nodeById = new Map(route.nodes.map((node) => [node.id, node]));
  return route.edges.some((edge) => {
    const fromNode = nodeById.get(edge.from);
    const toNode = nodeById.get(edge.to);
    if (!fromNode || !toNode) return false;

    return pageAreas.some((area) =>
      doesSegmentIntersectRing({
        segmentStart: fromNode,
        segmentEnd: toNode,
        ring: area.points,
      }),
    );
  });
};

// Mirrors the backend rule: excludes drop touching routes; any include area on a
// page flips it to keep only touching routes; exclude wins on overlap.
const shouldDropRoute = (
  route: ScaledRoute,
  pageGroup: PageAreaGroup,
): boolean => {
  if (isRouteTouchingAnyArea(route, pageGroup.excludeAreas)) return true;
  if (pageGroup.includeAreas.length === 0) return false;
  return !isRouteTouchingAnyArea(route, pageGroup.includeAreas);
};

export const filterExportRouteGroupsBySelectionAreas: FilterExportRouteGroupsBySelectionAreas =
  ({ routeGroups, selectionAreas }) => {
    const areasByPageIndex = new Map<number, PageAreaGroup>();
    for (const area of selectionAreas) {
      if (!area.appliesToRoutes || area.points.length < 3) continue;
      const pageGroup = areasByPageIndex.get(area.pageIndex) ?? {
        includeAreas: [],
        excludeAreas: [],
      };
      if (area.mode === "include") {
        pageGroup.includeAreas.push(area);
      } else {
        pageGroup.excludeAreas.push(area);
      }
      areasByPageIndex.set(area.pageIndex, pageGroup);
    }
    if (areasByPageIndex.size === 0) return routeGroups;

    const filteredGroups: PlanRouteGroupV3[] = [];
    for (const group of routeGroups) {
      const keptRoutes = group.routes.filter((route) => {
        const pageGroup = areasByPageIndex.get(route.page ?? 0);
        if (!pageGroup) return true;
        return !shouldDropRoute(route, pageGroup);
      });

      if (keptRoutes.length === 0) continue;
      if (keptRoutes.length === group.routes.length) {
        filteredGroups.push(group);
        continue;
      }
      filteredGroups.push({
        ...group,
        routes: keptRoutes,
        meters: keptRoutes.reduce((total, route) => total + route.meters, 0),
      });
    }
    return filteredGroups;
  };
